import throttle from '../../../assets/js/base/_throttle.js';

export class HeaderNavigation {
	/**
	 * Initialize a new Sections element
	 *
	 * @param {HTMLElement} node - the node that contains the root scrolling element
	 * @param {object} opts -
	 * 	- mode: snap, normal
	 *  - sticky: true
	 */
	constructor( node, opts ) {
		this.node = node;
		this.opts = {
			mode: 'snap', // snap to next item or free scroll
			back: '.header-navigation__scroll--back',
			forward: '.header-navigation__scroll--forward',
			content: '.header-navigation__content',
			items: '.header-navigation__item',
			sticky: true,
			...opts,
		};

		if ( ! this.node ) {
			return;
		}

		this.interval = null;
		this.back = this.node.querySelector( this.opts.back );
		this.forward = this.node.querySelector( this.opts.forward );
		this.content = this.node.querySelector( this.opts.content );
		this.items = Array.from( this.node.querySelectorAll( this.opts.items ) ).map( ( link ) => {
			let id = null;
			let target = null;

			if ( link.hash ) {
				id = link.hash.slice( 1 );
				target = document.getElementById( id );
			}

			return { id, link, target };
		} );

		window.addEventListener( 'resize', this.setState.bind( this ) );
		this.content.addEventListener( 'scroll', this.setState.bind( this ) );

		if ( 'snap' === this.opts.mode ) {
			this.forward.addEventListener( 'click', this.forwardIndex.bind( this ) );
			this.back.addEventListener( 'click', this.backIndex.bind( this ) );
		} else {
			this.forward.addEventListener( 'pointerdown', this.scrollForward.bind( this ) );
			this.forward.addEventListener( 'pointerup', this.scrollStop.bind( this ) );
			this.back.addEventListener( 'pointerdown', this.scrollBack.bind( this ) );
			this.back.addEventListener( 'pointerup', this.scrollStop.bind( this ) );
		}

		if ( this.opts.sticky ) {
			const onScrollActiveAnchor = () => {
				const { scrollTop, clientHeight } = document.documentElement;
				const absTarget = scrollTop + ( clientHeight / 3 ); // prettier-ignore
				const distances = this.items
					.filter( ( item ) => item.target )
					.reduce(
						( distances, item ) => ( {
							...distances,
							[ item.id ]: item.target ? Math.abs( item.target.getBoundingClientRect().top + scrollTop - absTarget ) : null,
						} ),
						{}
					);

				const closest = Object.keys( distances ).reduce(
					( closest, id ) => ( ! closest || ! distances[ closest ] || distances[ id ] < distances[ closest ] ? id : closest ),
					null
				);

				this.items.forEach( ( item ) => {
					if ( closest && item.id === closest ) {
						item.link.classList.add( 'header-navigation__item--active' );
					} else {
						item.link.classList.remove( 'header-navigation__item--active' );
					}
				} );
			};

			const onScrollFixed = () => {
				if ( this.node.getBoundingClientRect().top <= 0 ) {
					this.node.style.height = `${ this.node.offsetHeight }px`;
					this.node.classList.add( 'header-navigation--sticky' );
				} else {
					this.node.style.height = '';
					this.node.classList.remove( 'header-navigation--sticky' );
				}
			};

			window.addEventListener( 'scroll', throttle( onScrollActiveAnchor, 100 ) );
			window.addEventListener( 'scroll', throttle( onScrollFixed, 5 ) );
			onScrollActiveAnchor();
			onScrollFixed();
		}

		const onLinkClick = () => {
			// freeze the masthead temporarily while we click on TOC links cause to prevent the masthead overlaying the TOC while interacting with it
			if ( masthead && masthead.node.classList.contains( 'masthead--unpinned' ) ) {
				masthead.headroom.freeze();

				if ( typeof this.mastheadFreezeTimeout === 'number' ) {
					clearTimeout( this.mastheadFreezeTimeout );
					delete this.mastheadFreezeTimeout;
				}

				this.mastheadFreezeTimeout = setTimeout( () => {
					masthead.headroom.unfreeze();
					delete this.mastheadFreezeTimeout;
				}, 1500 );
			}
		};
		this.items.filter( ( item ) => item.target ).forEach( ( item ) => item.link.addEventListener( 'click', onLinkClick ) );

		let initialIndex = window.location.hash ? Math.max( 0, this.items.map( ( item ) => item.hash ).indexOf( window.location.hash ) ) : 0;
		this.setIndex( initialIndex );
		this.setState();
	}

	/**
	 * Scroll forward the scrollable content area
	 */
	scrollForward() {
		this.scrollInterval = setInterval( () => {
			this.content.scrollTo( { left: this.content.scrollLeft + 5 } );

			if ( this.forward.disabled ) {
				this.scrollStop();
			}
		}, 10 );
	}

	/**
	 * Scroll backwards the scrollable content area
	 */
	scrollBack() {
		this.scrollInterval = setInterval( () => {
			this.content.scrollTo( { left: Math.max( 0, this.content.scrollLeft - 5 ) } );

			if ( this.back.disabled ) {
				this.scrollStop();
			}
		}, 10 );
	}

	/**
	 * Stop the scrolling interval if it's active
	 */
	scrollStop() {
		if ( typeof this.scrollInterval === 'number' ) {
			clearInterval( this.scrollInterval );
		}

		this.scrollInterval = null;
	}

	setState() {
		const firstItem = this.items[ 0 ].link;
		const lastItem = this.items[ this.items.length - 1 ].link;

		if ( firstItem.offsetLeft - this.content.scrollLeft >= 0 ) {
			// first item is is view
			this.back.disabled = true;
			this.node.classList.remove( 'header-navigation--fade-in' );
		} else {
			// first item is outside the scroll area
			this.back.removeAttribute( 'disabled' );
			this.node.classList.add( 'header-navigation--fade-in' );
		}

		if ( this.content.scrollLeft + this.content.offsetWidth >= lastItem.offsetLeft + lastItem.offsetWidth - 1 ) {
			// the last item is fully in view
			this.forward.disabled = true;
			this.node.classList.remove( 'header-navigation--fade-out' );
		} else {
			// last item is outside the scroll area
			this.forward.removeAttribute( 'disabled' );
			this.node.classList.add( 'header-navigation--fade-out' );
		}
	}

	getIndex() {
		const distances = this.items.map( ( item ) => Math.abs( item.offsetLeft - this.content.scrollLeft ) );

		return distances.indexOf( Math.min( ...distances ) );
	}

	setIndex( index, behavior = 'smooth' ) {
		if ( index <= this.items.length ) {
			this.content.scrollTo( {
				left: this.items[ index ].offsetLeft,
				behavior,
			} );
		} else if ( interval > 0 ) {
			clearInterval( interval );
			interval = null;
		}
	}

	backIndex() {
		this.setIndex( Math.round( Math.max( this.getIndex() - 1, 0 ) ) );
	}

	forwardIndex() {
		this.setIndex( Math.round( Math.min( this.getIndex() + 1, this.items.length - 1 ) ) );
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	document.querySelectorAll( '.header-navigation' ).forEach( ( node ) => {
		new HeaderNavigation( node, { ...node.dataset } );
	} );
} );
